'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { useCompetitionLabels } from '@/components/social/competition/competition-labels';
import { competitionRankingMetric, type TradingCompetitionPhase, type TradingCompetitionSummary } from '@/components/social/competition/types';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Countdown } from '@/components/ui/countdown';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon, Calendar03Icon, ChampionIconSolid, ChartUpIcon, CheckmarkCircle02IconSolid, Clock04IconSolid, InformationCircleIcon, LinkForwardIconSolid, Menu01Icon } from '@/components/ui/icon/glyphs';
import { StatusPulse } from '@/components/ui/status-pulse';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { formatCompetitionEnd, formatCompetitionWindow } from '@/lib/competitions/format';
import { COMPETITIONS_PATH, competitionPath } from '@/lib/competitions/paths';
import { useFormattedCurrency } from '@/lib/currency';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal, type ShareSurface } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
export interface CompetitionHeroCarousel {
  count: number;
  index: number;
  previousTitle: string;
  nextTitle: string;
  onPrevious: () => void;
  onNext: () => void;
}
export type CompetitionHeroSize = 'compact' | 'full';
interface CompetitionHeroProps {
  competition: TradingCompetitionSummary;
  /** `compact` is the Social banner and list row; `full` is the competition page. */
  size: CompetitionHeroSize;
  /** Server-corrected current time supplied by the route so countdowns hydrate without changing text. */
  initialNowMs: number | null;
  timeZone: string;
  onOpenInfo: () => void;
  /** When set the whole hero is a link (stretched overlay); the action buttons stay clickable above it. */
  href?: string | undefined;
  carousel?: CompetitionHeroCarousel | undefined;
  className?: string | undefined;
}

// The approved event-hero treatment (frontend/src/components/live/event-page/event-hero.tsx):
// one 75% image, darker edge zones with a clearer centre, and the same display steps.
const FULL_SIZE_CLASS = 'h-[300px] @max-[767px]/competition-hero:h-auto @max-[767px]/competition-hero:min-h-[370px] @max-[767px]/competition-hero:gap-4'; // ARBITRARY: preserves the approved 300px desktop / 370px mobile baseline, letting wrapped mobile content grow.
// 128px desktop / 168px mobile: enough room between the action row and the countdown.
const COMPACT_SIZE_CLASS = 'h-32 @max-[767px]/competition-hero:h-42';
const FULL_TITLE_CLASS = 'text-[38px] leading-10 font-[680] tracking-[-0.045em] @max-[767px]/competition-hero:text-[31px] @max-[767px]/competition-hero:leading-[34px]'; // ARBITRARY: mirrors the approved event-details hero display sizes (38/40 desktop, 31/34 mobile).
const COMPACT_TITLE_CLASS = 'truncate text-heading-lg font-[680] tracking-[-0.045em] @max-[767px]/competition-hero:text-heading'; // ARBITRARY: the compact banner keeps the hero's weight and tracking one display step down.
const FULL_COUNTDOWN_CLASS = 'text-[36px] leading-[38px] @max-[767px]/competition-hero:text-[32px] @max-[767px]/competition-hero:leading-[34px]'; // ARBITRARY: mirrors the approved hero countdown display step (36/38 desktop, 32/34 mobile).
const COMPACT_COUNTDOWN_CLASS = 'text-heading-lg @max-[767px]/competition-hero:text-heading';
const CONCLUDED_TIME_CLASS = 'text-[26px] leading-8'; // ARBITRARY: mirrors the approved elapsed-time figure between heading-lg and display-sm.
const IMAGE_FILTER_CLASS = '[filter:saturate(.8)_contrast(1.05)]'; // ARBITRARY: the approved restrained saturation/contrast treatment.
const EDGE_OVERLAY_CLASS = '[background:linear-gradient(to_right,color-mix(in_oklch,var(--surface-scrim-strong)_72%,transparent)_0%,color-mix(in_oklch,var(--surface-scrim-default)_28%,transparent)_35%,color-mix(in_oklch,var(--surface-scrim-default)_10%,transparent)_52%,color-mix(in_oklch,var(--surface-scrim-default)_26%,transparent)_72%,color-mix(in_oklch,var(--surface-scrim-strong)_72%,transparent)_100%)]'; // ARBITRARY: the approved darker-edge/clear-centre readability gradient.
const GLASS_PILL_CLASS = 'inline-flex h-6 shrink-0 items-center gap-1.25 rounded-pill bg-surface-scrim-default px-2.25 text-caption font-semibold backdrop-blur-[12px]';
// Plain glyphs over the darkened banner: no plate, no blur (feedback 16 Sep 2026).
const GLASS_BUTTON_CLASS = 'rounded-full text-white';
function phaseConfiguration(phase: TradingCompetitionPhase, labels: Readonly<Record<TradingCompetitionPhase, string>>): {
  className: string;
  icon: IconSvgElement | null;
  label: string;
} {
  switch (phase) {
    case 'live':
      return {
        className: 'text-feedback-error-accent',
        icon: null,
        label: labels.live
      };
    case 'upcoming':
      return {
        className: 'text-white',
        icon: Clock04IconSolid,
        label: labels.upcoming
      };
    case 'concluded':
      return {
        className: 'text-white/70',
        icon: CheckmarkCircle02IconSolid,
        label: labels.concluded
      };
  }
}
function formatElapsed(endedAtMs: number, nowMs: number | null, locale: string): string {
  // Same placeholder as the countdown: the clock arrives after mount.
  if (nowMs === null) return '\u00A0';
  const elapsedMinutes = Math.max(0, Math.floor((nowMs - endedAtMs) / 60_000));
  const formatter = new Intl.RelativeTimeFormat(locale, {
    numeric: 'always'
  });
  if (elapsedMinutes >= 24 * 60) return formatter.format(-Math.floor(elapsedMinutes / (24 * 60)), 'day');
  if (elapsedMinutes >= 60) return formatter.format(-Math.floor(elapsedMinutes / 60), 'hour');
  return formatter.format(-elapsedMinutes, 'minute');
}
function PhasePill(t0) {
  const $ = _c(13);
  const {
    phase
  } = t0;
  const t = useTranslations("competitions.hero");
  let configuration;
  let t1;
  let t2;
  if ($[0] !== phase || $[1] !== t) {
    configuration = phaseConfiguration(phase, {
      live: t("live"),
      upcoming: t("upcoming"),
      concluded: t("concluded")
    });
    t1 = "competition-hero-phase";
    t2 = cn(GLASS_PILL_CLASS, configuration.className);
    $[0] = phase;
    $[1] = t;
    $[2] = configuration;
    $[3] = t1;
    $[4] = t2;
  } else {
    configuration = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== configuration.icon || $[6] !== phase) {
    t3 = phase === "live" ? <StatusPulse tone="error" className="size-2" /> : configuration.icon ? <Icon icon={configuration.icon} aria-hidden={true} className="size-3" /> : null;
    $[5] = configuration.icon;
    $[6] = phase;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== configuration.label || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <span data-testid={t1} className={t2}>{t3}{configuration.label}</span>;
    $[8] = configuration.label;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function CompetitionTag(t0) {
  const $ = _c(10);
  const {
    className
  } = t0;
  const t = useTranslations("competitions.hero");
  let t1;
  if ($[0] !== className) {
    t1 = cn(GLASS_PILL_CLASS, "min-w-0 text-white/70", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={ChampionIconSolid} aria-hidden={true} className="size-3 shrink-0" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("tag");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="truncate">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <span className={t1}>{t2}{t4}</span>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function PrizePill(t0) {
  const $ = _c(13);
  const {
    competition
  } = t0;
  const t = useTranslations("competitions.hero");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== competition.prizePoolD6 || $[1] !== formatD6) {
    t1 = formatD6(competition.prizePoolD6, "compact");
    $[0] = competition.prizePoolD6;
    $[1] = formatD6;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const amount = t1;
  let t2;
  if ($[3] !== competition.phase || $[4] !== t) {
    t2 = competition.phase === "concluded" ? t("paidOut") : t("prizePool");
    $[3] = competition.phase;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const label = t2;
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(GLASS_PILL_CLASS, "text-white");
    t4 = <Icon icon={ChampionIconSolid} aria-hidden={true} className="size-3 text-product-market-champion" />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== label) {
    t5 = <span className="sr-only">{label}</span>;
    $[8] = label;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== amount || $[11] !== t5) {
    t6 = <span data-testid="competition-hero-prize" className={t3}>{t4}{amount}{t5}</span>;
    $[10] = amount;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function PrizeMark(t0) {
  const $ = _c(14);
  const {
    competition
  } = t0;
  const t = useTranslations("competitions.hero");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== competition.prizePoolD6 || $[1] !== formatD6) {
    t1 = formatD6(competition.prizePoolD6, "compact");
    $[0] = competition.prizePoolD6;
    $[1] = formatD6;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const amount = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={ChampionIconSolid} aria-hidden={true} className="size-icon-lg text-product-market-champion" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== amount) {
    t3 = <span className="text-heading font-bold leading-none tracking-[-0.03em] tabular-nums text-white">{amount}</span>;
    $[4] = amount;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== competition.phase || $[7] !== t) {
    t4 = competition.phase === "concluded" ? t("paidOut") : t("prizePool");
    $[6] = competition.phase;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-caption font-medium leading-none text-white/70">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <span data-testid="competition-hero-prize" className="mb-2.5 inline-flex h-10.5 items-center gap-2 rounded-control bg-surface-scrim-default px-3.5 backdrop-blur-[12px]">{t2}<span className="inline-flex items-baseline gap-1.5">{t3}{t5}</span></span>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function CompetitionCountdown(t0) {
  const $ = _c(15);
  const {
    competition,
    initialNowMs,
    size
  } = t0;
  const locale = useLocale();
  const t = useTranslations("competitions.hero");
  const concluded = competition.phase === "concluded";
  const upcoming = competition.phase === "upcoming";
  let t1;
  if ($[0] !== competition.end || $[1] !== competition.start || $[2] !== concluded || $[3] !== initialNowMs || $[4] !== locale || $[5] !== size || $[6] !== t || $[7] !== upcoming) {
    const targetMs = Date.parse(upcoming ? competition.start : competition.end);
    const figureClass = cn("block font-bold tracking-[-0.05em] text-white", size === "full" ? FULL_COUNTDOWN_CLASS : COMPACT_COUNTDOWN_CLASS);
    let t2;
    if ($[9] !== concluded || $[10] !== t || $[11] !== upcoming) {
      t2 = concluded ? t("ended") : upcoming ? t("startsIn") : t("endsIn");
      $[9] = concluded;
      $[10] = t;
      $[11] = upcoming;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t2) {
      t3 = <span className="mb-1 block text-caption text-white/70">{t2}</span>;
      $[13] = t2;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t1 = <div className="shrink-0 text-right tabular-nums @max-[767px]/competition-hero:text-left">{t3}{concluded ? <strong className={cn(figureClass, size === "full" && CONCLUDED_TIME_CLASS)}>{formatElapsed(targetMs, initialNowMs, locale)}</strong> : <Countdown targetMs={targetMs} initialNowMs={initialNowMs} endedLabel={t("ended")} ariaLabel={countdown => upcoming ? t("startCountdownLabel", {
        countdown
      }) : t("countdownLabel", {
        countdown
      })} className={figureClass} />}</div>;
    $[0] = competition.end;
    $[1] = competition.start;
    $[2] = concluded;
    $[3] = initialNowMs;
    $[4] = locale;
    $[5] = size;
    $[6] = t;
    $[7] = upcoming;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function CarouselNav(t0) {
  const $ = _c(18);
  const {
    carousel
  } = t0;
  const t = useTranslations("competitions.hero");
  if (carousel.count <= 1) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-white/30 @max-[320px]/competition-hero:hidden" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== carousel.previousTitle || $[2] !== t) {
    t2 = t("previous", {
      title: carousel.previousTitle
    });
    $[1] = carousel.previousTitle;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(GLASS_BUTTON_CLASS, "text-white/60");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== carousel.onPrevious || $[6] !== t2) {
    t4 = <AnimatedButton variant="tertiary" size="sm" prefix={ArrowLeft01Icon} aria-label={t2} className={t3} onClick={carousel.onPrevious} />;
    $[5] = carousel.onPrevious;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== carousel.nextTitle || $[9] !== t) {
    t5 = t("next", {
      title: carousel.nextTitle
    });
    $[8] = carousel.nextTitle;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn(GLASS_BUTTON_CLASS, "text-white/60");
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== carousel.onNext || $[13] !== t5) {
    t7 = <AnimatedButton variant="tertiary" size="sm" prefix={ArrowRight01Icon} aria-label={t5} className={t6} onClick={carousel.onNext} />;
    $[12] = carousel.onNext;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t7) {
    t8 = <div className="flex items-center @max-[280px]/competition-hero:hidden" data-testid="competition-hero-carousel-nav">{t1}{t4}{t7}</div>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export function CompetitionHero(t0) {
  const $ = _c(135);
  const {
    competition,
    size,
    initialNowMs,
    timeZone,
    onOpenInfo,
    href,
    carousel,
    className
  } = t0;
  const locale = useLocale();
  const t = useTranslations("competitions.hero");
  const tShare = useTranslations("competitions.share");
  const labels = useCompetitionLabels();
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    network
  } = useUserNetwork();
  const intent = useIntentRoutePrefetch(href);
  const compact = size === "compact";
  let t1;
  if ($[0] !== competition || $[1] !== locale || $[2] !== timeZone) {
    t1 = formatCompetitionWindow(competition, locale, timeZone);
    $[0] = competition;
    $[1] = locale;
    $[2] = timeZone;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const scheduleLabel = t1;
  let t2;
  if ($[4] !== competition.board || $[5] !== labels) {
    t2 = labels.board(competition.board);
    $[4] = competition.board;
    $[5] = labels;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== competition || $[8] !== labels) {
    t3 = labels.metric(competitionRankingMetric(competition));
    $[7] = competition;
    $[8] = labels;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const boardLabel = `${t2} · ${t3}`;
  const t4 = competition.id;
  const t5 = competition.title;
  const t6 = competition.phase;
  let t7;
  if ($[10] !== competition.prizePoolD6 || $[11] !== formatD6) {
    t7 = formatD6(competition.prizePoolD6, "compact");
    $[10] = competition.prizePoolD6;
    $[11] = formatD6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== competition.end || $[14] !== locale || $[15] !== timeZone) {
    t8 = formatCompetitionEnd(competition.end, locale, timeZone);
    $[13] = competition.end;
    $[14] = locale;
    $[15] = timeZone;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== competition.id || $[18] !== competition.phase || $[19] !== competition.title || $[20] !== t7 || $[21] !== t8) {
    t9 = {
      kind: "competition",
      competitionId: t4,
      title: t5,
      phase: t6,
      prizeLabel: t7,
      endLabel: t8
    };
    $[17] = competition.id;
    $[18] = competition.phase;
    $[19] = competition.title;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const shareSurface = t9;
  let t10;
  bb0: {
    if (typeof window === "undefined") {
      t10 = "";
      break bb0;
    }
    let t11;
    if ($[23] !== competition.id || $[24] !== network) {
      const url = new URL(competitionPath(competition.id), window.location.origin);
      withDemoMode(url, network === "mainnet" ? "mainnet" : "devnet");
      t11 = url.toString();
      $[23] = competition.id;
      $[24] = network;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = t11;
  }
  const shareUrl = t10;
  const titleId = `competition-hero-title-${competition.id}`;
  const t11 = carousel !== undefined && carousel.count > 1 ? "carousel" : undefined;
  let t12;
  if ($[26] !== carousel || $[27] !== t) {
    t12 = carousel !== undefined && carousel.count > 1 ? t("carouselLabel", {
      index: carousel.index + 1,
      count: carousel.count
    }) : undefined;
    $[26] = carousel;
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = compact ? "p-4 @max-[767px]/competition-hero:p-3.5" : "p-5 @max-[767px]/competition-hero:p-4";
  const t14 = compact ? COMPACT_SIZE_CLASS : FULL_SIZE_CLASS;
  let t15;
  if ($[29] !== className || $[30] !== t13 || $[31] !== t14) {
    t15 = cn("group/competition-hero relative isolate flex w-full flex-col overflow-hidden rounded-card bg-surface-scrim-strong text-white", t13, t14, className);
    $[29] = className;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("absolute inset-0 opacity-75", IMAGE_FILTER_CLASS);
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = !compact;
  let t18;
  if ($[34] !== competition.bannerImageUrl || $[35] !== t17) {
    t18 = <div data-testid="competition-hero-banner" className={t16}><OptimizedImage variant="hero" src={competition.bannerImageUrl} alt="" priority={t17} sizes="(min-width: 768px) 100vw, calc(100vw - 32px)" loadReveal="none" nsfw={false} preview={false} /></div>;
    $[34] = competition.bannerImageUrl;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", EDGE_OVERLAY_CLASS)} />;
    t20 = <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linear-to-b from-transparent via-surface-scrim-default/20 to-surface-scrim-strong/50" />;
    $[37] = t19;
    $[38] = t20;
  } else {
    t19 = $[37];
    t20 = $[38];
  }
  let t21;
  if ($[39] !== competition.title || $[40] !== href || $[41] !== intent || $[42] !== t) {
    t21 = href !== undefined ? <Link href={href} prefetch={false} {...intent} aria-label={t("open", {
      title: competition.title
    })} data-testid="competition-hero-link" className="absolute inset-0 z-3 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/50" /> : null;
    $[39] = competition.title;
    $[40] = href;
    $[41] = intent;
    $[42] = t;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== compact || $[45] !== competition) {
    t22 = <div className="flex min-w-0 items-center gap-1.75">{compact ? <><CompetitionTag className="@max-[767px]/competition-hero:hidden" /><PrizePill competition={competition} /><PhasePill phase={competition.phase} /></> : <><PhasePill phase={competition.phase} /><CompetitionTag className="shrink" /></>}</div>;
    $[44] = compact;
    $[45] = competition;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t) {
    t23 = t("about");
    $[47] = t;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = cn(GLASS_BUTTON_CLASS, "group-data-[size=compact]/competition-hero:@max-[400px]/competition-hero:hidden", "group-data-[size=full]/competition-hero:@max-[440px]/competition-hero:hidden");
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== onOpenInfo || $[51] !== t23) {
    t25 = <AnimatedButton variant="tertiary" size="sm" prefix={InformationCircleIcon} aria-label={t23} onClick={onOpenInfo} className={t24} />;
    $[50] = onOpenInfo;
    $[51] = t23;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== tShare) {
    t26 = tShare("title");
    $[53] = tShare;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== competition.title || $[56] !== tShare) {
    t27 = tShare("text", {
      title: competition.title
    });
    $[55] = competition.title;
    $[56] = tShare;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== tShare) {
    t28 = tShare("button");
    $[58] = tShare;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = cn(GLASS_BUTTON_CLASS, "group-data-[size=compact]/competition-hero:@max-[360px]/competition-hero:hidden", "group-data-[size=full]/competition-hero:@max-[400px]/competition-hero:hidden");
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t28) {
    t30 = <AnimatedButton variant="tertiary" size="sm" prefix={LinkForwardIconSolid} aria-label={t28} className={t29} />;
    $[61] = t28;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== shareSurface || $[64] !== shareUrl || $[65] !== t26 || $[66] !== t27 || $[67] !== t30) {
    t31 = <ShareModal surface={shareSurface} url={shareUrl} title={t26} text={t27} trigger={t30} />;
    $[63] = shareSurface;
    $[64] = shareUrl;
    $[65] = t26;
    $[66] = t27;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t) {
    t32 = t("allCompetitions");
    $[69] = t;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = cn(GLASS_BUTTON_CLASS, "aspect-square size-8 min-h-8 min-w-8 gap-0 p-0", "@max-[320px]/competition-hero:hidden");
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t) {
    t34 = t("allCompetitions");
    $[72] = t;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Icon icon={Menu01Icon} className="size-icon-md shrink-0" aria-hidden={true} />;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== t34) {
    t36 = <Link href={COMPETITIONS_PATH} aria-label={t34}>{t35}</Link>;
    $[75] = t34;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== t32 || $[78] !== t36) {
    t37 = <AnimatedButton asChild={true} variant="tertiary" size="sm" aria-label={t32} className={t33} data-testid="competition-hero-all">{t36}</AnimatedButton>;
    $[77] = t32;
    $[78] = t36;
    $[79] = t37;
  } else {
    t37 = $[79];
  }
  let t38;
  if ($[80] !== carousel) {
    t38 = carousel !== undefined ? <CarouselNav carousel={carousel} /> : null;
    $[80] = carousel;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  let t39;
  if ($[82] !== t25 || $[83] !== t31 || $[84] !== t37 || $[85] !== t38) {
    t39 = <div className="pointer-events-auto flex shrink-0 items-center gap-1">{t25}{t31}{t37}{t38}</div>;
    $[82] = t25;
    $[83] = t31;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t22 || $[88] !== t39) {
    t40 = <div className="pointer-events-none relative z-4 flex items-center justify-between gap-5 @max-[360px]/competition-hero:gap-3">{t22}{t39}</div>;
    $[87] = t22;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== compact || $[91] !== competition) {
    t41 = compact ? null : <PrizeMark competition={competition} />;
    $[90] = compact;
    $[91] = competition;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  let t42;
  if ($[93] !== compact || $[94] !== competition.title || $[95] !== titleId) {
    t42 = compact ? <h2 id={titleId} className={COMPACT_TITLE_CLASS}>{competition.title}</h2> : <h1 id={titleId} className={FULL_TITLE_CLASS}>{competition.title}</h1>;
    $[93] = compact;
    $[94] = competition.title;
    $[95] = titleId;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  let t43;
  if ($[97] !== compact || $[98] !== competition.subtitle) {
    t43 = !compact && competition.subtitle ? <p className="mt-1.75 text-body leading-5 text-white/70 @max-[767px]/competition-hero:text-caption">{competition.subtitle}</p> : null;
    $[97] = compact;
    $[98] = competition.subtitle;
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  const t44 = compact ? "mt-1 @max-[767px]/competition-hero:hidden" : "mt-2.75";
  let t45;
  if ($[100] !== t44) {
    t45 = cn("flex flex-wrap items-center gap-x-1.75 gap-y-0.5 text-caption leading-4 text-white/70", t44);
    $[100] = t44;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <Icon icon={Calendar03Icon} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  let t47;
  if ($[103] !== scheduleLabel) {
    t47 = <span>{scheduleLabel}</span>;
    $[103] = scheduleLabel;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  let t49;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <span aria-hidden="true" className="inline-flex size-3 items-center justify-center">•</span>;
    t49 = <Icon icon={ChartUpIcon} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[105] = t48;
    $[106] = t49;
  } else {
    t48 = $[105];
    t49 = $[106];
  }
  let t50;
  if ($[107] !== boardLabel) {
    t50 = <span data-testid="competition-hero-board">{boardLabel}</span>;
    $[107] = boardLabel;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== t45 || $[110] !== t47 || $[111] !== t50) {
    t51 = <div className={t45}>{t46}{t47}{t48}{t49}{t50}</div>;
    $[109] = t45;
    $[110] = t47;
    $[111] = t50;
    $[112] = t51;
  } else {
    t51 = $[112];
  }
  let t52;
  if ($[113] !== t41 || $[114] !== t42 || $[115] !== t43 || $[116] !== t51) {
    t52 = <div className="min-w-0 max-w-180 @max-[767px]/competition-hero:w-full">{t41}{t42}{t43}{t51}</div>;
    $[113] = t41;
    $[114] = t42;
    $[115] = t43;
    $[116] = t51;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== competition || $[119] !== initialNowMs || $[120] !== size) {
    t53 = <CompetitionCountdown competition={competition} initialNowMs={initialNowMs} size={size} />;
    $[118] = competition;
    $[119] = initialNowMs;
    $[120] = size;
    $[121] = t53;
  } else {
    t53 = $[121];
  }
  let t54;
  if ($[122] !== t52 || $[123] !== t53) {
    t54 = <div className="pointer-events-none relative z-2 flex min-w-0 flex-1 items-end justify-between gap-10 @max-[767px]/competition-hero:flex-col @max-[767px]/competition-hero:items-start @max-[767px]/competition-hero:justify-end @max-[767px]/competition-hero:gap-2.5">{t52}{t53}</div>;
    $[122] = t52;
    $[123] = t53;
    $[124] = t54;
  } else {
    t54 = $[124];
  }
  let t55;
  if ($[125] !== size || $[126] !== t11 || $[127] !== t12 || $[128] !== t15 || $[129] !== t18 || $[130] !== t21 || $[131] !== t40 || $[132] !== t54 || $[133] !== titleId) {
    t55 = <div className="@container/competition-hero w-full"><section data-testid="competition-hero" data-size={size} aria-labelledby={titleId} aria-roledescription={t11} aria-label={t12} className={t15}>{t18}{t19}{t20}{t21}{t40}{t54}</section></div>;
    $[125] = size;
    $[126] = t11;
    $[127] = t12;
    $[128] = t15;
    $[129] = t18;
    $[130] = t21;
    $[131] = t40;
    $[132] = t54;
    $[133] = titleId;
    $[134] = t55;
  } else {
    t55 = $[134];
  }
  return t55;
}